import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FilePlus2,
  ShieldCheck,
  Hash,
  CalendarDays,
  Shield,
  FileText,
  ArrowRight,
  MapPin,
  AlertCircle,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  User,
  Users,
  Phone,
  Mail,
  Bus,
  Car,
  Image as ImageIcon,
  Eye,
  X,
} from "lucide-react";
import ClaimStatusBadge from "../../components/claims/ClaimStatusBadge";
import ClaimProgressResponsive from "../../components/claims/ClaimProgressResponsive";
import claimService from "../../api/ClaimService";
import { toast } from "react-toastify";
import Loader from "../../components/misc/Loader";
import { formatMoney } from "../../utils/formatMoney";
import moment from "moment";

const PAGE_SIZE = 8;

const formatDate = (dateString) => {
  if (dateString == null || dateString === "") return "N/A";
  const parsed = moment(dateString);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY") : "N/A";
};

const formatDateTime = (dateString) => {
  if (dateString == null || dateString === "") return "N/A";
  const parsed = moment(dateString);
  return parsed.isValid() ? parsed.format("DD MMM, YYYY HH:mm") : "N/A";
};

const formatPolicyType = (type) => {
  if (type === "okada_rider") return "Motor Rider";
  if (!type) return "Insurance";
  return type.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const formatDuration = (coverage) => {
  if (!coverage || typeof coverage !== "object") return null;
  if (coverage.display_value != null && coverage.display_unit) {
    const unit = String(coverage.display_unit);
    const value = coverage.display_value;
    const singular = value === 1 ? unit.replace(/s$/, "") : unit;
    return `${value} ${singular}`;
  }
  if (coverage.hours) return `${coverage.hours} hours`;
  return null;
};

const customerName = (customer = {}) =>
  [customer.first_name, customer.last_name].filter(Boolean).join(" ").trim() ||
  customer.full_name ||
  "Policyholder";

const parseClaimParts = (claim) => {
  const raw = claim?.incident;
  const items = Array.isArray(raw) ? raw : raw && typeof raw === "object" ? [raw] : [];
  const byKey = (key) => items.find((item) => item?.key === key) || {};
  const incident = byKey("incident");
  const purchased = byKey("purchased_policy");
  const customer = byKey("customer");
  const hasKeyedIncident = Object.keys(incident).length > 0;
  const fallback = !hasKeyedIncident && items[0] && !items[0].key ? items[0] : incident;
  return {
    incident: hasKeyedIncident ? incident : fallback,
    purchased,
    customer,
  };
};

const getDocuments = (claim) =>
  (Array.isArray(claim?.documents) && claim.documents.length
    ? claim.documents
    : claim?.supporting_documents) || [];

const isImageUrl = (url = "") => /\.(png|jpe?g|gif|webp|bmp)(\?|$)/i.test(url);

const purchasedSummary = (purchased = {}, policyType) => {
  const duration = formatDuration(purchased.coverageDuration);
  const amount = purchased.amount ?? purchased.coverageDuration?.price;
  const parts = [];

  if (purchased.vehicleName) parts.push(purchased.vehicleName);
  if (purchased.licensePlate) parts.push(purchased.licensePlate);
  if (purchased.vehicleType) parts.push(purchased.vehicleType);
  if (purchased.busLicensePlate) parts.push(purchased.busLicensePlate);
  if (purchased.routeDestination) parts.push(purchased.routeDestination);
  if (purchased.tripType) parts.push(formatPolicyType(purchased.tripType));
  if (!parts.length && policyType) parts.push(formatPolicyType(policyType));
  if (duration) parts.push(duration);
  if (amount != null && amount !== "") parts.push(formatMoney(amount));

  return parts.join(" · ") || "Purchased cover";
};

const EmptyClaimsState = ({ onMakeClaim, onBrowse }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    className="flex flex-col justify-center items-center px-4 py-16"
  >
    <div className="flex justify-center items-center mb-6 w-24 h-24 bg-emerald-50 rounded-3xl border border-emerald-100">
      <Shield className="w-10 h-10 text-emerald-600" />
    </div>
    <h3 className="mb-2 text-2xl font-bold text-gray-900">No claims yet</h3>
    <p className="mb-8 max-w-md text-center text-gray-600">
      When you file a claim for yourself or for someone else, it will appear here with live status updates.
    </p>
    <div className="flex flex-col gap-3 sm:flex-row">
      <button
        type="button"
        onClick={onMakeClaim}
        className="inline-flex gap-2 justify-center items-center px-6 py-3 font-semibold text-white bg-emerald-600 rounded-xl shadow-sm transition-colors cursor-pointer hover:bg-emerald-700"
      >
        <FilePlus2 size={18} />
        Make your first claim
        <ArrowRight size={16} />
      </button>
      <button
        type="button"
        onClick={onBrowse}
        className="inline-flex gap-2 justify-center items-center px-6 py-3 font-semibold text-gray-700 bg-white rounded-xl border border-gray-200 transition-colors cursor-pointer hover:bg-gray-50"
      >
        <Shield size={18} />
        Browse policies
      </button>
    </div>
  </motion.div>
);

const MetaChip = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-sm text-gray-600">
    <Icon size={14} className="text-gray-400" />
    <span>{children}</span>
  </span>
);

const DetailField = ({ label, children }) =>
  children ? (
    <div>
      <p className="mb-1 text-[11px] font-semibold tracking-wide text-gray-400 uppercase">{label}</p>
      <div className="text-sm font-medium text-gray-900">{children}</div>
    </div>
  ) : null;

const ClaimingForChip = ({ forSelf }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full whitespace-nowrap ${
      forSelf
        ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
        : "bg-orange-50 text-orange-700 ring-1 ring-orange-100"
    }`}
  >
    {forSelf ? <User size={12} /> : <Users size={12} />}
    {forSelf ? "Myself" : "Someone else"}
  </span>
);

const ClaimMobileCard = ({ claim, selected, onView }) => {
  const policy = claim.insurance_policy || {};
  const { incident, purchased } = parseClaimParts(claim);
  const forSelf = claim.for_self !== false;
  const representative = claim.representative;

  return (
    <article
      onClick={onView}
      className={`touch-manipulation overflow-hidden bg-white rounded-2xl border shadow-sm cursor-pointer transition-colors ${
        selected ? "border-emerald-300 ring-1 ring-emerald-100" : "border-gray-200"
      }`}
    >
      <div className={`h-1 ${forSelf ? "bg-emerald-500" : "bg-orange-500"}`} />
      <div className="p-4 space-y-3">
        <div className="flex gap-3 justify-between items-start">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">
              {policy.policy_name || "Insurance claim"}
            </h3>
            <p className="mt-0.5 text-xs text-gray-500">
              {formatPolicyType(policy.policy_type)} · #{claim.slug?.slice(0, 8).toUpperCase() || "N/A"}
            </p>
          </div>
          <ClaimStatusBadge status={claim.claim_status} />
        </div>

        <div>
          <ClaimingForChip forSelf={forSelf} />
          {!forSelf && (
            <p className="mt-1 text-xs text-gray-600">
              {representative?.full_name || "Someone else"}
              {representative?.relationship ? ` · ${representative.relationship}` : ""}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Incident</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{incident.type || "—"}</p>
            <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">{incident.location || "—"}</p>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Date</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{formatDate(incident.date)}</p>
          </div>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Cover</p>
          <p className="mt-1 text-sm text-gray-700">{purchasedSummary(purchased, policy.policy_type)}</p>
        </div>

        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onView();
          }}
          className="inline-flex gap-1.5 justify-center items-center w-full min-h-11 text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-xl transition-colors cursor-pointer hover:bg-emerald-100"
        >
          <Eye size={16} />
          View details
        </button>
      </div>
    </article>
  );
};

const ClaimsPagination = ({ currentPage, totalPages, totalItems, onPageChange }) => (
  <div className="flex flex-col gap-3 justify-between items-center px-4 py-3 sm:flex-row">
    <p className="text-sm text-gray-600">
      Showing {(currentPage - 1) * PAGE_SIZE + 1} to{" "}
      {Math.min(currentPage * PAGE_SIZE, totalItems)} of {totalItems} claims
    </p>
    <div className="flex gap-2 items-center">
      <button
        type="button"
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border border-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
        aria-label="First page"
      >
        <ChevronsLeft size={16} />
      </button>
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="p-2 rounded-lg border border-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
        aria-label="Previous page"
      >
        <ChevronLeft size={16} />
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
        .map((page, idx, arr) => {
          const showEllipsis = arr[idx - 1] && page - arr[idx - 1] > 1;
          return (
            <React.Fragment key={page}>
              {showEllipsis && <span className="px-2 text-gray-500">...</span>}
              <button
                type="button"
                onClick={() => onPageChange(page)}
                aria-current={currentPage === page ? "page" : undefined}
                className={`min-w-[2.25rem] min-h-11 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  currentPage === page
                    ? "bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-md"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            </React.Fragment>
          );
        })}
      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage >= totalPages}
        className="p-2 rounded-lg border border-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
        aria-label="Next page"
      >
        <ChevronRight size={16} />
      </button>
      <button
        type="button"
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage >= totalPages}
        className="p-2 rounded-lg border border-gray-300 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white"
        aria-label="Last page"
      >
        <ChevronsRight size={16} />
      </button>
    </div>
  </div>
);

const ClaimsList = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [claims, setClaims] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [whoFilter, setWhoFilter] = useState("all");
  const [selectedClaim, setSelectedClaim] = useState(null);

  useEffect(() => {
    const fetchClaims = async () => {
      setIsLoading(true);
      try {
        const response = await claimService.getClaims();
        if (response.status && response.data?.status_code === 200) {
          let claimsData = response.data.data;
          if (claimsData && typeof claimsData === "object" && !Array.isArray(claimsData)) {
            claimsData = Object.values(claimsData);
          }
          setClaims(claimsData || []);
        } else {
          toast.error(response.message || "An error occurred while fetching claims");
        }
      } catch (error) {
        toast.error(
          error.response?.data?.reason || "An error occurred while fetching claims"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchClaims();
  }, []);

  const filteredClaims = useMemo(() => {
    if (whoFilter === "self") return claims.filter((claim) => claim.for_self !== false);
    if (whoFilter === "other") return claims.filter((claim) => claim.for_self === false);
    return claims;
  }, [claims, whoFilter]);

  const totalPages = Math.ceil(filteredClaims.length / PAGE_SIZE) || 1;
  const pageClaims = filteredClaims.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setCurrentPage(1);
  }, [whoFilter]);

  useEffect(() => {
    if (!selectedClaim) return undefined;
    const stillVisible = filteredClaims.some(
      (claim) => (claim.slug || claim.id) === (selectedClaim.slug || selectedClaim.id)
    );
    if (!stillVisible) setSelectedClaim(null);
  }, [filteredClaims, selectedClaim]);

  useEffect(() => {
    if (!selectedClaim) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedClaim(null);
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [selectedClaim]);

  if (isLoading) {
    return <Loader fullScreen={true} text="Loading claims..." />;
  }

  const selfCount = claims.filter((claim) => claim.for_self !== false).length;
  const otherCount = claims.filter((claim) => claim.for_self === false).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-white">
      <div className="pt-24 lg:pt-28" />
      <div className="px-4 mx-auto max-w-8xl sm:px-6 lg:px-8 pb-16">
        <div className="flex flex-wrap gap-4 justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 sm:text-3xl">Claims</h1>
            <p className="mt-1 text-sm text-gray-600 sm:text-base">
              Track claims you filed for yourself or for someone else
            </p>
          </div>
          {claims.length > 0 && (
            <button
              type="button"
              onClick={() => navigate("/my-policies")}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-700 to-green-800 shadow-md hover:from-emerald-800 hover:to-green-900 cursor-pointer"
            >
              <FilePlus2 size={16} />
              Make Claim
            </button>
          )}
        </div>

        {claims.length === 0 ? (
          <EmptyClaimsState
            onMakeClaim={() => navigate("/my-policies")}
            onBrowse={() => navigate("/insurance/choose")}
          />
        ) : (
          <>
            <div
              className="flex flex-wrap gap-2 mt-6"
              role="tablist"
              aria-label="Filter claims"
            >
              {[
                { id: "all", label: "All claims", count: claims.length },
                { id: "self", label: "For myself", count: selfCount },
                { id: "other", label: "For someone else", count: otherCount },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={whoFilter === tab.id}
                  onClick={() => setWhoFilter(tab.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium rounded-full border transition-colors cursor-pointer ${
                    whoFilter === tab.id
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-emerald-300 hover:text-emerald-700"
                  }`}
                >
                  {tab.label}
                  <span
                    className={`min-w-[1.25rem] px-1.5 text-[11px] font-semibold rounded-full ${
                      whoFilter === tab.id ? "bg-white/20 text-white" : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {filteredClaims.length === 0 ? (
              <div className="p-10 mt-8 text-center bg-white rounded-2xl border border-gray-200">
                <p className="text-gray-600">No claims in this filter.</p>
              </div>
            ) : (
              <>
                <div className="hidden overflow-hidden mt-8 bg-white rounded-2xl border border-gray-200 shadow-sm lg:block">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[860px] text-left">
                      <thead className="bg-emerald-50/80 border-b border-emerald-100">
                        <tr>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">Policy</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">Claiming for</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">Incident</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">Cover</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">Date</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase">Status</th>
                          <th className="px-4 py-3 text-xs font-semibold tracking-wide text-gray-500 uppercase text-right"> </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {pageClaims.map((claim, index) => {
                          const policy = claim.insurance_policy || {};
                          const { incident, purchased } = parseClaimParts(claim);
                          const forSelf = claim.for_self !== false;
                          const representative = claim.representative;
                          const isSelected = selectedClaim?.slug === claim.slug;
                          return (
                            <tr
                              key={claim.slug || claim.id || index}
                              onClick={() => setSelectedClaim(claim)}
                              className={`cursor-pointer transition-colors ${
                                isSelected ? "bg-emerald-50/70" : "hover:bg-emerald-50/40"
                              }`}
                            >
                              <td className="px-4 py-3.5">
                                <p className="text-sm font-semibold text-gray-900">{policy.policy_name || "Insurance claim"}</p>
                                <p className="mt-0.5 text-xs text-gray-500">
                                  {formatPolicyType(policy.policy_type)} · #{claim.slug?.slice(0, 8).toUpperCase() || "N/A"}
                                </p>
                              </td>
                              <td className="px-4 py-3.5">
                                <ClaimingForChip forSelf={forSelf} />
                                {!forSelf && (
                                  <p className="mt-1 text-xs text-gray-500 line-clamp-1">
                                    {representative?.full_name || "Someone else"}
                                    {representative?.relationship ? ` · ${representative.relationship}` : ""}
                                  </p>
                                )}
                              </td>
                              <td className="px-4 py-3.5">
                                <p className="text-sm font-medium text-gray-900">{incident.type || "—"}</p>
                                <p className="mt-0.5 text-xs text-gray-500 line-clamp-1">{incident.location || "—"}</p>
                              </td>
                              <td className="px-4 py-3.5 max-w-[220px]">
                                <p className="text-sm text-gray-700 line-clamp-2">
                                  {purchasedSummary(purchased, policy.policy_type)}
                                </p>
                              </td>
                              <td className="px-4 py-3.5 whitespace-nowrap">
                                <p className="text-sm text-gray-900">{formatDate(incident.date)}</p>
                              </td>
                              <td className="px-4 py-3.5">
                                <ClaimStatusBadge status={claim.claim_status} />
                              </td>
                              <td className="px-4 py-3.5 text-right">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelectedClaim(claim);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-lg transition-colors cursor-pointer hover:bg-emerald-100"
                                >
                                  <Eye size={14} />
                                  View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="bg-gray-50/80 border-t border-gray-100">
                    <ClaimsPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={filteredClaims.length}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </div>

                <div className="mt-6 space-y-3 lg:hidden">
                  {pageClaims.map((claim, index) => (
                    <ClaimMobileCard
                      key={claim.slug || claim.id || index}
                      claim={claim}
                      selected={selectedClaim?.slug === claim.slug}
                      onView={() => setSelectedClaim(claim)}
                    />
                  ))}
                  <div className="overflow-hidden bg-white rounded-2xl border border-gray-200">
                    <ClaimsPagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={filteredClaims.length}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>

      <ClaimDetailsDrawer claim={selectedClaim} onClose={() => setSelectedClaim(null)} />
    </div>
  );
};

const ClaimDetailsDrawer = ({ claim, onClose }) => {
  const policy = claim?.insurance_policy || {};
  const { incident, purchased, customer } = parseClaimParts(claim || {});
  const documents = getDocuments(claim);
  const forSelf = claim?.for_self !== false;
  const representative = claim?.representative;
  const duration = formatDuration(purchased.coverageDuration);
  const amount = purchased.amount ?? purchased.coverageDuration?.price;
  const description =
    incident.description &&
    !String(incident.description).toLowerCase().includes("insurance provider information is missing")
      ? incident.description
      : null;

  return (
    <AnimatePresence>
      {claim && (
        <>
          <motion.div
            key="claim-drawer-overlay"
            className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.aside
            key="claim-drawer-panel"
            className="fixed inset-y-0 right-0 z-[70] flex flex-col w-full max-w-2xl h-full bg-white shadow-2xl"
            initial={{ x: 420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 420, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="claim-drawer-title"
          >
            <div className={`h-1 ${forSelf ? "bg-emerald-500" : "bg-orange-500"}`} />
            <div className="flex gap-4 justify-between items-start p-5 border-b border-gray-200/70">
              <div className="flex flex-1 gap-3 items-start min-w-0">
                <div
                  className={`grid flex-shrink-0 place-items-center w-11 h-11 text-white rounded-xl shadow-sm ${
                    forSelf
                      ? "bg-gradient-to-br from-emerald-500 to-green-600"
                      : "bg-gradient-to-br from-orange-500 to-amber-600"
                  }`}
                >
                  {forSelf ? <User size={18} /> : <Users size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-2 items-center mb-1">
                    <h2 id="claim-drawer-title" className="text-lg font-bold text-gray-900">
                      {policy.policy_name || "Insurance claim"}
                    </h2>
                    <ClaimStatusBadge status={claim.claim_status} />
                  </div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 items-center">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded-full ${
                        forSelf
                          ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100"
                          : "bg-orange-50 text-orange-700 ring-1 ring-orange-100"
                      }`}
                    >
                      {forSelf ? "For myself" : "For someone else"}
                    </span>
                    <MetaChip icon={Shield}>{formatPolicyType(policy.policy_type)}</MetaChip>
                    <MetaChip icon={Hash}>#{claim.slug?.slice(0, 8).toUpperCase() || "N/A"}</MetaChip>
                  </div>
                  {!forSelf && representative?.full_name && (
                    <p className="mt-2 text-sm text-gray-600">
                      This claim is for <span className="font-semibold text-gray-900">{representative.full_name}</span>
                      {representative.relationship ? ` · ${representative.relationship}` : ""}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="flex-shrink-0 p-2 text-gray-500 rounded-lg transition-colors cursor-pointer hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close claim details"
              >
                <X size={20} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-4 bg-gray-50">
              <div className="p-4 bg-white rounded-xl border border-gray-200">
                <ClaimProgressResponsive status={claim.claim_status} variant="purple" />
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="p-3 rounded-xl bg-red-50/80 border border-red-100">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-red-500">Incident</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{incident.type || "—"}</p>
                  <p className="mt-0.5 text-xs text-gray-600">{formatDate(incident.date)}</p>
                </div>
                <div className="p-3 rounded-xl bg-white border border-gray-100">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Where</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900">{incident.location || "—"}</p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-50/80 border border-emerald-100">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Cover used</p>
                  <p className="mt-1 text-sm font-semibold text-gray-900 line-clamp-2">
                    {purchasedSummary(purchased, policy.policy_type)}
                  </p>
                </div>
              </div>

              <section className="p-5 bg-white rounded-xl border border-gray-200">
                <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                  <AlertCircle size={16} className="text-red-600" />
                  What happened
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailField label="Type">{incident.type}</DetailField>
                  <DetailField label="Date">{formatDate(incident.date)}</DetailField>
                  <DetailField label="Location">
                    {incident.location ? (
                      <span className="inline-flex gap-1.5 items-center">
                        <MapPin size={14} className="text-gray-400" />
                        {incident.location}
                      </span>
                    ) : null}
                  </DetailField>
                  <DetailField label="Authorities notified">
                    {incident.authorities
                      ? String(incident.authorities).toUpperCase() === "YES" || incident.authorities === "yes"
                        ? "Yes"
                        : "No"
                      : null}
                  </DetailField>
                  {description && (
                    <div className="sm:col-span-2">
                      <DetailField label="Description">{description}</DetailField>
                    </div>
                  )}
                </div>
              </section>

              <section className="p-5 bg-white rounded-xl border border-gray-200">
                <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                  {policy.policy_type === "intercity_travel" ? (
                    <Bus size={16} className="text-emerald-600" />
                  ) : (
                    <Car size={16} className="text-emerald-600" />
                  )}
                  Cover this claim is for
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailField label="Policy">{policy.policy_name}</DetailField>
                  <DetailField label="Type">{formatPolicyType(policy.policy_type)}</DetailField>
                  <DetailField label="Vehicle">{purchased.vehicleName || purchased.vehicleType}</DetailField>
                  <DetailField label="Plate">{purchased.licensePlate || purchased.busLicensePlate}</DetailField>
                  <DetailField label="Route">
                    {purchased.routeDestination ||
                      (purchased.departureCity && purchased.destinationCity
                        ? `${purchased.departureCity} → ${purchased.destinationCity}`
                        : null)}
                  </DetailField>
                  <DetailField label="Trip type">
                    {purchased.tripType ? formatPolicyType(purchased.tripType) : null}
                  </DetailField>
                  <DetailField label="From">
                    {purchased.departureCity
                      ? `${purchased.departureCity}${purchased.departureRegion ? `, ${purchased.departureRegion}` : ""}`
                      : null}
                  </DetailField>
                  <DetailField label="To">
                    {purchased.destinationCity
                      ? `${purchased.destinationCity}${purchased.destinationRegion ? `, ${purchased.destinationRegion}` : ""}`
                      : null}
                  </DetailField>
                  <DetailField label="Duration">{duration}</DetailField>
                  <DetailField label="Amount paid">
                    {amount != null && amount !== "" ? formatMoney(amount) : null}
                  </DetailField>
                  <DetailField label="Purchased">
                    {purchased.coverageDuration?.purchased_at
                      ? formatDateTime(purchased.coverageDuration.purchased_at)
                      : purchased.coverageDuration?.purchase_date
                        ? formatDate(purchased.coverageDuration.purchase_date)
                        : null}
                  </DetailField>
                </div>
              </section>

              {!forSelf && representative && (
                <section className="p-5 bg-orange-50/50 rounded-xl border border-orange-100">
                  <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                    <Users size={16} className="text-orange-600" />
                    Who this claim is for
                  </h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <DetailField label="Name">{representative.full_name}</DetailField>
                    <DetailField label="Relationship to you">{representative.relationship}</DetailField>
                    <DetailField label="Phone">{representative.phone}</DetailField>
                    <DetailField label="Email">{representative.email}</DetailField>
                    <DetailField label="ID type">{representative.id_type}</DetailField>
                    <DetailField label="ID number">{representative.id_number}</DetailField>
                  </div>
                  {(representative.id_front || representative.id_back) && (
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {representative.id_front && (
                        <a
                          href={representative.id_front}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="overflow-hidden rounded-lg border border-orange-200 bg-white cursor-pointer hover:border-orange-400"
                        >
                          <img src={representative.id_front} alt="ID front" className="object-cover w-full h-28" />
                          <p className="px-2 py-1.5 text-[11px] font-medium text-center text-gray-600">ID front</p>
                        </a>
                      )}
                      {representative.id_back && (
                        <a
                          href={representative.id_back}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="overflow-hidden rounded-lg border border-orange-200 bg-white cursor-pointer hover:border-orange-400"
                        >
                          <img src={representative.id_back} alt="ID back" className="object-cover w-full h-28" />
                          <p className="px-2 py-1.5 text-[11px] font-medium text-center text-gray-600">ID back</p>
                        </a>
                      )}
                    </div>
                  )}
                </section>
              )}

              <section className="p-5 bg-white rounded-xl border border-gray-200">
                <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                  <User size={16} className="text-emerald-600" />
                  Policyholder
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <DetailField label="Name">{customerName(customer)}</DetailField>
                  <DetailField label="Phone">
                    {customer.phone_number ? (
                      <span className="inline-flex gap-1.5 items-center">
                        <Phone size={14} className="text-gray-400" />
                        {customer.phone_number}
                      </span>
                    ) : null}
                  </DetailField>
                  <DetailField label="Email">
                    {customer.email ? (
                      <span className="inline-flex gap-1.5 items-center break-all">
                        <Mail size={14} className="text-gray-400" />
                        {customer.email}
                      </span>
                    ) : null}
                  </DetailField>
                </div>
              </section>

              {documents.length > 0 && (
                <section className="p-5 bg-white rounded-xl border border-gray-200">
                  <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                    <FileText size={16} className="text-emerald-600" />
                    Supporting documents
                  </h3>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {documents.map((doc, idx) => (
                      <a
                        key={`${doc}-${idx}`}
                        href={doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="overflow-hidden rounded-xl border border-gray-200 bg-gray-50 transition-colors cursor-pointer hover:border-emerald-300 hover:bg-emerald-50 group"
                      >
                        {isImageUrl(doc) ? (
                          <img src={doc} alt={`Document ${idx + 1}`} className="object-cover w-full h-24" />
                        ) : (
                          <div className="grid place-items-center h-24 text-gray-400 group-hover:text-emerald-600">
                            <FileText size={28} />
                          </div>
                        )}
                        <div className="flex gap-1 justify-center items-center px-2 py-2 text-[11px] font-medium text-gray-600 group-hover:text-emerald-700">
                          {isImageUrl(doc) ? <ImageIcon size={12} /> : <FileText size={12} />}
                          Document {idx + 1}
                          <ExternalLink size={11} />
                        </div>
                      </a>
                    ))}
                  </div>
                </section>
              )}

              {policy.insurance_coverage_list?.length > 0 && (
                <section className="p-5 bg-white rounded-xl border border-gray-200">
                  <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                    <ShieldCheck size={16} className="text-emerald-600" />
                    Policy benefits
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {policy.insurance_coverage_list.map((cover, idx) => (
                      <div key={`${cover.title}-${idx}`} className="p-3 rounded-lg bg-emerald-50/60 border border-emerald-100">
                        <p className="text-sm font-semibold text-gray-900">{cover.title}</p>
                        {cover.description && <p className="mt-1 text-xs text-gray-600">{cover.description}</p>}
                        {cover.limit && (
                          <p className="mt-1 text-xs font-semibold text-emerald-700">
                            Limit {formatMoney(cover.limit)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section className="p-5 bg-white rounded-xl border border-gray-200">
                <h3 className="flex gap-2 items-center mb-4 text-sm font-bold text-gray-900">
                  <CalendarDays size={16} className="text-emerald-600" />
                  Claim timeline
                </h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <DetailField label="Issued">
                    {formatDateTime(claim.claimed_issued_at ?? claim.claim_issued_at)}
                  </DetailField>
                  <DetailField label="Approved">{formatDateTime(claim.approved_at)}</DetailField>
                  <DetailField label="Rejected">{formatDateTime(claim.rejected_at)}</DetailField>
                </div>
              </section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default ClaimsList;
